import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { pvKostenRechnerApi } from '../../api/pvKostenRechnerApi'
import { ApiError } from '../../api/httpClient'
import type { PvAveragePriceResponse } from '../../dto/shared/PvKostenRechnerDto'
import { postalCodeMatchesCountry } from '../../utils/country'
import { formatEuro } from '../../utils/format'
import { clampMin } from '../../utils/number'
import { track } from '../../analytics/track'
import { getAnonymousToolId } from '../../utils/anonymousToolId'
import { CheckCircleIcon } from './icons'
import type { SupportedLanguage } from '../../i18n'

/** pSEO-Story Task 07 (E-E-A-T Methodik-Transparenz-Seite) - one slug per language, same
 *  convention as SeoLandingPage.tsx's FAQ_TITLE/RELATED_LINKS_TITLE Records, since the widget
 *  itself has no access to a LandingPageContent object to read a slug from (it's embedded in
 *  bespoke pages too, e.g. /pv-kostenrechner, not just SeoLandingPage-rendered ones). */
const METHODOLOGY_PAGE_SLUG: Record<SupportedLanguage, string> = {
  de: 'methodik-preisberechnung',
  es: 'como-calculamos-el-precio',
  pl: 'jak-obliczamy-cene',
}

export interface PvKostenRechnerValues {
  postalCode: string
  systemSizeKwp: number
  /** null = "kein Speicher" (Toggle aktiv). */
  batterySizeKwh: number | null
}

/** Never a real German postal code (no real PLZ starts with "00" - the range is 01067-99998) -
 *  used only for the one-shot pSEO bootstrap calculation below, so the backend's region-matching
 *  deterministically falls back to a nationwide estimate (regionUsed=false) instead of ever
 *  accidentally prefix-matching a real region. Never shown in the postal-code input itself. */
const BOOTSTRAP_POSTAL_CODE = '00000'

export interface PvKostenRechnerWidgetProps {
  /** Wired by the caller to the actual "Auftrag erstellen"-Flow (Login-Status-Weiche, Prefill über
   *  Registrierung hinweg - siehe tasks/new-things/preis-durchschnittsrechner/
   *  04-cta-prefill-deep-link.md). Dieses Widget selbst hält nur den lokalen State und reicht ihn
   *  beim Klick weiter, baut den Mechanismus selbst nicht. */
  onRequestOffer: (values: PvKostenRechnerValues) => void
  /** pSEO-Story Task 02: pre-fills systemSizeKwp/batterySizeKwh from a landing page's
   *  URL-matching axis (see content/landingPages/types.ts CalculatorAxis) and, when initialKwp is
   *  given, triggers one extra bootstrap calculation on mount using BOOTSTRAP_POSTAL_CODE - the
   *  pSEO requirement (seo-next.md "Preisrechner") is that such a page never looks empty: a
   *  visitor sees a real, data-derived price immediately, before touching anything. The postal
   *  code *input* itself always starts empty regardless of these props - typing a real one runs a
   *  normal, regionally-aware calculation exactly like today (see the debounced effect below),
   *  which then takes over from the bootstrap estimate (see userInteractedRef). Omit both props
   *  entirely for today's unprefilled usage (main landing page / /pv-kostenrechner) - behavior
   *  there is completely unchanged. */
  initialKwp?: number
  initialStorageKwh?: number
}

/** Debounce vor der automatischen Berechnung (kein "Berechnen"-Button, siehe 02-frontend-rechner-
 *  widget.md) - gleicher Wert/gleiche Namenskonvention wie PvSizingTool.DEBOUNCE_MS. */
const DEBOUNCE_MS = 400

/** Public, anonymous, stateless average-price estimate widget (tasks/new-things/preis-
 *  durchschnittsrechner/02-frontend-rechner-widget.md) - calls only POST /tools/pv-average-price,
 *  nothing is ever persisted. Reused on the landing page (03), its own SEO page (05) and, since
 *  pSEO-Story Task 02, embedded (pre-filled) in programmatic landing pages via SeoLandingPage. */
export function PvKostenRechnerWidget({ onRequestOffer, initialKwp, initialStorageKwh }: PvKostenRechnerWidgetProps) {
  const { t, i18n } = useTranslation()
  const [postalCode, setPostalCode] = useState('')
  const [systemSizeKwp, setSystemSizeKwp] = useState<number | ''>(initialKwp ?? '')
  const [batterySizeKwh, setBatterySizeKwh] = useState<number | ''>(initialStorageKwh ?? '')
  const [noBattery, setNoBattery] = useState(initialKwp !== undefined && initialStorageKwh === undefined)

  const [result, setResult] = useState<PvAveragePriceResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // Fires once per mount, not on every debounced recalculation as the user tweaks inputs - "used
  // the calculator" should count a visitor once, not once per keystroke-triggered recalculation.
  const trackedUsageRef = useRef(false)
  // Guards the one-shot pSEO bootstrap calculation below against clobbering a fresher, real
  // (user-entered postal code) result if the user starts typing before the bootstrap request
  // resolves - set the moment any field is touched, checked in the bootstrap's .then()/.catch().
  const userInteractedRef = useRef(false)

  const postalCodeValid = postalCode.trim() !== '' && postalCodeMatchesCountry(postalCode, 'DE')
  const batteryFilled = noBattery || batterySizeKwh !== ''
  const allFilled = postalCodeValid && systemSizeKwp !== '' && systemSizeKwp > 0 && batteryFilled

  function buildValues(): PvKostenRechnerValues {
    return {
      postalCode: postalCode.trim(),
      systemSizeKwp: systemSizeKwp === '' ? 0 : systemSizeKwp,
      batterySizeKwh: noBattery || batterySizeKwh === '' ? null : batterySizeKwh,
    }
  }

  useEffect(() => {
    if (!allFilled) {
      setResult(null)
      setError(null)
      return
    }

    setLoading(true)
    const timeout = setTimeout(() => {
      pvKostenRechnerApi
        .calculate({ ...buildValues(), anonymousId: getAnonymousToolId() })
        .then((response) => {
          setResult(response)
          setError(null)
          if (!trackedUsageRef.current) {
            trackedUsageRef.current = true
            track('pv_kostenrechner_used', {
              insufficientData: response.insufficientData,
              roughEstimate: response.roughEstimate,
            })
          }
        })
        .catch((err: unknown) => {
          setResult(null)
          setError(err instanceof ApiError ? err.message : t('landing.priceCalculator.error'))
        })
        .finally(() => setLoading(false))
    }, DEBOUNCE_MS)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, systemSizeKwp, batterySizeKwh, noBattery, allFilled])

  // One-shot pSEO bootstrap (see PvKostenRechnerWidgetProps.initialKwp doc) - independent of the
  // interactive effect above, which stays gated on allFilled (i.e. a real user-entered postal
  // code) exactly as before: postalCode always starts empty here regardless of prefill, so
  // allFilled is false at mount even when initialKwp is set, and the effect above is a no-op then.
  useEffect(() => {
    if (initialKwp === undefined || initialKwp <= 0) return

    setLoading(true)
    pvKostenRechnerApi
      .calculate({
        postalCode: BOOTSTRAP_POSTAL_CODE,
        systemSizeKwp: initialKwp,
        batterySizeKwh: initialStorageKwh ?? null,
        anonymousId: getAnonymousToolId(),
      })
      .then((response) => {
        if (userInteractedRef.current) return
        setResult(response)
        setError(null)
        if (!trackedUsageRef.current) {
          trackedUsageRef.current = true
          track('pv_kostenrechner_used', {
            insufficientData: response.insufficientData,
            roughEstimate: response.roughEstimate,
          })
        }
      })
      .catch((err: unknown) => {
        if (userInteractedRef.current) return
        setResult(null)
        setError(err instanceof ApiError ? err.message : t('landing.priceCalculator.error'))
      })
      .finally(() => {
        if (!userInteractedRef.current) setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trustItems = t('landing.priceCalculator.trust', { returnObjects: true }) as string[]

  return (
    <div className="pv-kosten-rechner-widget">
      <form className="pv-kosten-rechner-form" onSubmit={(e) => e.preventDefault()}>
        <label>
          {t('landing.priceCalculator.postalCodeLabel')}
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            value={postalCode}
            onChange={(e) => {
              userInteractedRef.current = true
              setPostalCode(e.target.value)
            }}
          />
          {postalCode.trim() !== '' && !postalCodeValid && (
            <span className="pv-kosten-rechner-hint">{t('landing.priceCalculator.postalCodeFormatWarning')}</span>
          )}
        </label>

        <label>
          {t('landing.priceCalculator.systemSizeLabel')}
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={systemSizeKwp}
            onChange={(e) => {
              userInteractedRef.current = true
              setSystemSizeKwp(e.target.value === '' ? '' : clampMin(Number(e.target.value), 0.1))
            }}
          />
        </label>

        <label>
          {t('landing.priceCalculator.batterySizeLabel')}
          <input
            type="number"
            min={0}
            step={0.5}
            disabled={noBattery}
            value={noBattery ? '' : batterySizeKwh}
            onChange={(e) => {
              userInteractedRef.current = true
              setBatterySizeKwh(e.target.value === '' ? '' : clampMin(Number(e.target.value), 0))
            }}
          />
        </label>

        <label className="pv-kosten-rechner-checkbox">
          <input
            type="checkbox"
            checked={noBattery}
            onChange={(e) => {
              userInteractedRef.current = true
              setNoBattery(e.target.checked)
              if (e.target.checked) setBatterySizeKwh('')
            }}
          />
          {t('landing.priceCalculator.noBatteryLabel')}
        </label>
      </form>

      <ul className="trust-row pv-kosten-rechner-trust">
        {trustItems.map((item) => (
          <li key={item}>
            <CheckCircleIcon /> {item}
          </li>
        ))}
      </ul>

      {loading && <p className="pv-kosten-rechner-status">{t('landing.priceCalculator.loading')}</p>}
      {error && <p className="pv-kosten-rechner-error">{error}</p>}

      {result && !result.insufficientData && !result.roughEstimate && (
        <div className="pv-kosten-rechner-result">
          <div className="pv-kosten-rechner-average">
            <span>{t('landing.priceCalculator.result.averageLabel')}</span>
            <strong>{formatEuro(result.averagePriceEur ?? 0)}</strong>
          </div>
          <ul className="kv-list">
            <li>
              <span>{t('landing.priceCalculator.result.rangeLabel')}</span>
              <strong>
                {formatEuro(result.minPriceEur ?? 0)} – {formatEuro(result.maxPriceEur ?? 0)}
              </strong>
            </li>
            <li>
              <span>{t('landing.priceCalculator.result.pricePerKwpLabel')}</span>
              <strong>{formatEuro(result.pricePerKwp ?? 0)}</strong>
            </li>
            <li>
              <span>{t('landing.priceCalculator.result.offerCountLabel')}</span>
              <strong>{result.offerCount}</strong>
            </li>
          </ul>
          <p className="pv-kosten-rechner-region-hint">
            {result.regionUsed
              ? t('landing.priceCalculator.result.regionalHint', { count: result.offerCount })
              : t('landing.priceCalculator.result.nationwideHint')}
          </p>
        </div>
      )}

      {result && result.roughEstimate && (
        <div className="pv-kosten-rechner-result">
          <div className="pv-kosten-rechner-average">
            <span>{t('landing.priceCalculator.result.averageLabel')}</span>
            <strong>{formatEuro(result.averagePriceEur ?? 0)}</strong>
          </div>
          <ul className="kv-list">
            <li>
              <span>{t('landing.priceCalculator.result.pricePerKwpLabel')}</span>
              <strong>{formatEuro(result.pricePerKwp ?? 0)}</strong>
            </li>
            <li>
              <span>{t('landing.priceCalculator.result.offerCountLabel')}</span>
              <strong>{result.offerCount}</strong>
            </li>
          </ul>
          <p className="pv-kosten-rechner-region-hint pv-kosten-rechner-rough-hint">
            {t('landing.priceCalculator.result.roughEstimateHint')}
          </p>
        </div>
      )}

      {result && result.insufficientData && (
        <div className="pv-kosten-rechner-insufficient">
          <p>{t('landing.priceCalculator.result.insufficientDataText')}</p>
        </div>
      )}

      {result && (
        <div className="pv-kosten-rechner-footer">
          <p className="pv-kosten-rechner-growing-data-hint">{t('landing.priceCalculator.result.growingDataHint')}</p>
          <p className="pv-kosten-rechner-disclaimer">
            {t('landing.priceCalculator.result.disclaimer')}{' '}
            <Link to={`/${METHODOLOGY_PAGE_SLUG[i18n.language as SupportedLanguage] ?? METHODOLOGY_PAGE_SLUG.de}`}>
              {t('landing.priceCalculator.result.methodologyLinkLabel')}
            </Link>
          </p>
          <button type="button" className="btn-primary" onClick={() => onRequestOffer(buildValues())}>
            {t('landing.priceCalculator.cta')}
          </button>
        </div>
      )}
    </div>
  )
}
